import { useCallback, useMemo, useRef } from "react";
import SideMenuItem from "./SideMenuItem";
import { formatGroupName, groupChatsByDate } from "../../helpers";
import { CircularProgress, Typography } from "@mui/material";
import { InfiniteData } from "@tanstack/react-query";
import { Record } from "./constants";

type ChatListProps = {
  data?: InfiniteData<
    { records: Record[];
      pagination: { currentPage: number; totalPages: number };
    }>;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetching?: boolean;
  clearAllChatData: () => void;
};

const ChatList = ({ data, fetchNextPage, hasNextPage, isFetching, clearAllChatData }: ChatListProps) => {
  const groupedChats = useMemo(() => groupChatsByDate(data?.pages), [data]);
  const chatContainerRef = useRef(null);

  const observerRef = useRef<IntersectionObserver | null>(null);

  const lastItemRef = useCallback((node: HTMLElement | null) => {
    if (isFetching) return;
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasNextPage) {
        fetchNextPage();
      }
    });

    if (node) observerRef.current.observe(node);
  }, [fetchNextPage, hasNextPage, isFetching]);
  return (
    <div
      ref={chatContainerRef}
      className="chat-container"
    >
      {Object.entries(groupedChats).map(([groupName, chats]) => (
        chats.length > 0 && (
          <div key={groupName} className="mb-4">
            <Typography variant="overline/regular-400" className="ps-2 tracking-[1px] dark:text-mui-white-70">
              {formatGroupName(groupName)}
            </Typography>
            <div className="flex flex-col py-2 gap-y-2">
              {chats.map((record, idx) => {
                const isLast = idx === chats.length - 1;
                return (
                  <SideMenuItem
                    key={record.sessionId}
                    clearAllChatData={clearAllChatData}
                    item={record}
                    chatRef={isLast ? lastItemRef : null} // Attach only to the last item
                  />
                );
              })}
            </div>
          </div>
        )
      ))}

      {isFetching
      && (
        <div className="h-full flex items-center justify-center">
          <CircularProgress size={16} disableShrink color="inherit" className="text-tgpt-secondary-main text-center" />
        </div>
      )}
    </div>
  );
};

export default ChatList;
